import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useSyncReload } from './hooks/useSyncReload';
import Home from './pages/Home';
import Articles from './pages/Articles';
import DailyCharts from './pages/DailyCharts';
import OnChain from './pages/OnChain';
import Newsletter from './pages/Newsletter';
import Contact from './pages/Contact';
import About from './pages/About';
import Privacy from './pages/Privacy';
import Navbar from './components/Navbar';
import TickerBar from './components/TickerBar';

// Path → page name mapping (for Navbar active state)
const pathToPage = (pathname: string): string => {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/articles')) return 'articles';
  if (pathname.startsWith('/charts')) return 'charts';
  if (pathname === '/on-chain') return 'onchain';
  if (pathname === '/newsletter') return 'newsletter';
  if (pathname === '/contact') return 'contact';
  if (pathname === '/about') return 'about';
  if (pathname === '/privacy') return 'privacy';
  return 'home';
};

// Page name → path mapping
const pageToPath: Record<string, string> = {
  home: '/',
  articles: '/articles',
  charts: '/charts',
  onchain: '/on-chain',
  newsletter: '/newsletter',
  contact: '/contact',
  about: '/about',
  privacy: '/privacy',
};

const AppContent: React.FC = () => {
  useSyncReload();
  const navigate = useNavigate();
  const location = useLocation();

  const currentPage = pathToPage(location.pathname);

  const handleNavigate = (page: string, articleId?: string) => {
    if (page === 'articles' && articleId) {
      navigate(`/articles/${articleId}`);
    } else {
      navigate(pageToPath[page] ?? '/');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Scroll to top on every route change (covers Link clicks, back/forward, programmatic nav)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  return (
    <HelmetProvider>
      {/* Global sticky chrome — always on top */}
      <Navbar currentPage={currentPage} onNavigate={handleNavigate} />
      {/* Ticker: fixed below navbar. h-[44px] is explicit so wrapper never collapses */}
      <div className="fixed top-16 h-[44px] left-0 right-0 z-40">
        <TickerBar />
      </div>
      {/* Content offset: navbar 64px + ticker 44px + 8px buffer = 116px */}
      <div className="pt-[116px]">
        <Routes>
          <Route path="/" element={<Home currentPage={currentPage} onNavigate={handleNavigate} />} />
          <Route path="/articles" element={<Articles onNavigate={handleNavigate} />} />
          <Route path="/articles/:id" element={<Articles onNavigate={handleNavigate} />} />
          <Route path="/charts" element={<DailyCharts onNavigate={handleNavigate} />} />
          <Route path="/charts/:id" element={<DailyCharts onNavigate={handleNavigate} />} />
          <Route path="/on-chain" element={<OnChain onNavigate={handleNavigate} />} />
          <Route path="/newsletter" element={<Newsletter onNavigate={handleNavigate} />} />
          <Route path="/contact" element={<Contact onNavigate={handleNavigate} />} />
          <Route path="/about" element={<About onNavigate={handleNavigate} />} />
          <Route path="/privacy" element={<Privacy onNavigate={handleNavigate} />} />
          {/* Fallback */}
          <Route path="*" element={<Home currentPage={currentPage} onNavigate={handleNavigate} />} />
        </Routes>
      </div>
    </HelmetProvider>
  );
};

const App: React.FC = () => (
  <BrowserRouter>
    <AppContent />
  </BrowserRouter>
);

export default App;
